// MACHINERY V2 (Stroje) — SR create/edit: the DÁTUM A ČAS pair (M2.6).
//
// Spec: data package §7, rows `openDate (date)` and `openDate (time)`.
// Reference impl: design_handoff_machinery_v3/machinery-sr.jsx lines 565-583 —
// one FormField holding a `1fr 160px` grid, each half carrying its own mono
// sublabel (DÁTUM OTVORENIA / ČAS). Artboards 0702 / 0703.
//
// TWO CONTROLS, ONE FIELD. Both Controllers bind `openDate` and each reads the
// value from its OWN `field.value`, so neither needs a `watch`. The date half
// writes the day and keeps the time, the time half writes hours/minutes and
// keeps the day. That is v1's structure (two Controllers on the same name,
// CreateServiceReportPage.tsx:595-597 and :653-655) and it is what the shared
// `openDate` column requires. The conversion — INCLUDING the deliberate
// "date shows today while the value is undefined, time shows empty" quirk — is
// in srDateTime.ts; read its header before changing anything here.
//
// MUI-FREE, BY THE MANAGER'S PRE-APPROVED FALLBACK: native `type="date"` /
// `type="time"` in the v2 38px well. The browser supplies the calendar and the
// clock; the artboard's field shape, sublabels, mono value and teal focus are
// ours. The native `max` mirrors v1's `disableFuture`, and the RHF `validate`
// rule below is still the authority (a typed-in future date is caught there).
//
// PHONE COLLAPSES THE GRID to a single column — §15 / §7 ("Phone collapses date
// /time grid"), via the shared `useBreakpoint` hook. NEVER MUI `useMediaQuery`:
// the data package flags v1's `useMediaQuery('(max-width:768px)')` on this very
// surface as the thing to replace.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import { Control, Controller } from "react-hook-form";

import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { ChangeServiceReport } from "../../models/ServiceReportsModel";
import FormField from "../forms/FormField";
import { CalendarGlyph } from "../forms/FormGlyphs";
import {
  applyDateInput,
  applyTimeInput,
  dateInputValue,
  isFutureDate,
  timeInputValue,
  todayInputValue,
} from "./srDateTime";

export interface SrDateTimeFieldProps {
  control: Control<ChangeServiceReport>;
  error?: string;
}

const SrDateTimeField: React.FC<SrDateTimeFieldProps> = ({ control, error }) => {
  const { translate } = useTranslation(["myMachinery", "common"]);
  const { isPhone } = useBreakpoint();

  return (
    <FormField
      glyph={<CalendarGlyph />}
      label={translate("myMachinery:dateAndTimeSection")}
      error={error}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isPhone ? "1fr" : "1fr 160px",
          gap: 12,
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div className="mv2-sublabel">{translate("myMachinery:openDate")}</div>
          <Controller
            control={control}
            name="openDate"
            rules={{
              validate: (value) =>
                isFutureDate(value) ? translate("myMachinery:openDateFuture") : undefined,
            }}
            render={({ field }) => (
              <input
                id="sr-open-date"
                type="date"
                className={"mv2-input" + (error ? " is-invalid" : "")}
                style={{ fontFamily: "var(--font-mono)" }}
                aria-label={translate("myMachinery:openDate")}
                aria-invalid={!!error}
                max={todayInputValue()}
                value={dateInputValue(field.value)}
                onChange={(e) => field.onChange(applyDateInput(field.value, e.target.value))}
                onBlur={field.onBlur}
              />
            )}
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="mv2-sublabel">{translate("common:common.time")}</div>
          <Controller
            control={control}
            name="openDate"
            render={({ field }) => (
              <input
                id="sr-open-time"
                type="time"
                className="mv2-input"
                style={{ fontFamily: "var(--font-mono)" }}
                aria-label={translate("common:common.time")}
                placeholder="hh:mm"
                value={timeInputValue(field.value)}
                onChange={(e) => field.onChange(applyTimeInput(field.value, e.target.value))}
                onBlur={field.onBlur}
              />
            )}
          />
        </div>
      </div>
    </FormField>
  );
};

export default SrDateTimeField;
